/**
 * Pure functions of the `mastermind` type: the progress blob, the submission and how a
 * server verdict folds back into the progress (ADR 0041, 0055). No component here, so
 * `module.ts` stays light in the eager bundle.
 */
import type { Feedback, Verdict } from "../types";
import {
  DEFAULT_COLORS,
  DEFAULT_LENGTH,
  colorsOf,
  lengthOf,
  repeatsOf,
  type Guess,
  type MastermindDefinition,
  type MastermindProgress,
  type MastermindSubmission,
} from "./types";

export function emptyDefinition(): MastermindDefinition {
  return {
    puzzleType: "mastermind",
    typeVersion: 1,
    length: DEFAULT_LENGTH,
    colors: DEFAULT_COLORS,
    repeats: true,
    rulesText: {
      en: "Break the hidden code of coloured pegs. Each guess is answered with key pegs: a black peg for every peg of the right colour in the right place, a white peg for every other peg of a right colour in the wrong place. The key pegs never say which pegs they stand for. Guesses are unlimited: only the time counts.",
      fr: "Percez le code secret de pions colorés. Chaque proposition reçoit des fiches : une noire par pion de la bonne couleur à la bonne place, une blanche par autre pion d'une bonne couleur mal placé. Les fiches ne disent jamais de quels pions elles parlent. Propositions illimitées : seul le temps compte.",
    },
  };
}

export function emptyDraft(definition: MastermindDefinition): (number | null)[] {
  return Array.from({ length: lengthOf(definition) }, () => null);
}

export function emptyProgress(definition: MastermindDefinition): MastermindProgress {
  return { version: 1, draft: emptyDraft(definition), guesses: [] };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isColor(value: unknown, colors: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= colors;
}

function isCount(value: unknown, length: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= length;
}

function asGuess(value: unknown, definition: MastermindDefinition): Guess | null {
  const length = lengthOf(definition);
  if (!isRecord(value) || !Array.isArray(value.code) || value.code.length !== length) return null;
  const colors = colorsOf(definition);
  if (!value.code.every((c) => isColor(c, colors))) return null;
  if (!isCount(value.black, length) || !isCount(value.white, length)) return null;
  if (value.black + value.white > length) return null;
  return { code: [...(value.code as number[])], black: value.black, white: value.white };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: MastermindDefinition,
  raw: unknown,
): MastermindProgress {
  if (!isRecord(raw)) return emptyProgress(definition);
  const colors = colorsOf(definition);
  const stored = Array.isArray(raw.draft) ? raw.draft : [];
  const draft = emptyDraft(definition).map((_, i) =>
    isColor(stored[i], colors) ? (stored[i] as number) : null,
  );
  const guesses = Array.isArray(raw.guesses)
    ? raw.guesses.map((g) => asGuess(g, definition)).filter((g): g is Guess => g !== null)
    : [];
  return { version: 1, draft, guesses };
}

/** The filled pegs, in order: an incomplete draft is refused by the Player before this. */
export function serializeSubmission(
  _definition: MastermindDefinition,
  progress: MastermindProgress,
): MastermindSubmission {
  return { code: progress.draft.filter((c): c is number => c !== null) };
}

export function isComplete(draft: (number | null)[]): draft is number[] {
  return draft.every((c) => c !== null);
}

export function hasRepeat(code: readonly (number | null)[]): boolean {
  const filled = code.filter((c) => c !== null);
  return new Set(filled).size !== filled.length;
}

export function sameCode(a: readonly number[], b: readonly (number | null)[]): boolean {
  return a.length === b.length && a.every((c, i) => c === b[i]);
}

export function isSolved(definition: MastermindDefinition, progress: MastermindProgress): boolean {
  return progress.guesses.some((g) => g.black === lengthOf(definition));
}

/** Whether a guess breaks the puzzle's own rule (a repeat where none is allowed). */
export function breaksRepeatRule(
  definition: MastermindDefinition,
  code: readonly (number | null)[],
): boolean {
  return !repeatsOf(definition) && hasRepeat(code);
}

function pegsOf(feedback: Feedback[]): { black: number; white: number } | null {
  const item = feedback.find((f) => f.code === "pegs");
  const params = item?.params ?? {};
  if (!item || typeof params.black !== "number" || typeof params.white !== "number") return null;
  return { black: params.black, white: params.white };
}

/**
 * Folds one verdict into the progress. An answered guess joins the history (once) and the
 * draft empties; the found code joins it with every peg black. Anything else (an incomplete
 * guess, a colour out of range) changes nothing.
 */
export function applyVerdict(
  definition: MastermindDefinition,
  progress: MastermindProgress,
  verdict: Verdict<MastermindSubmission>,
): MastermindProgress {
  const code = verdict.submission.code;
  const length = lengthOf(definition);
  if (code.length !== length) return progress;
  const known = progress.guesses.some((g) => sameCode(g.code, code));
  const draft = emptyDraft(definition);
  if (verdict.correct) {
    if (known) return { ...progress, draft };
    return {
      ...progress,
      draft,
      guesses: [...progress.guesses, { code, black: length, white: 0 }],
    };
  }
  const pegs = pegsOf(verdict.feedback);
  if (!pegs) return progress;
  if (known) return { ...progress, draft };
  return { ...progress, draft, guesses: [...progress.guesses, { code, ...pegs }] };
}
